/**
 * ADAPTER SELECTION — the only module allowed to import an adapter.
 *
 * Switching the whole application from fixtures to the API is this:
 *
 *   .env.local
 *     VITE_DATA_SOURCE=http
 *     VITE_API_BASE_URL=https://api.example.internal/v1
 *
 * No component changes. That is the contract this file exists to hold, and an
 * ESLint rule (`no-restricted-imports`) stops anything else importing
 * `@/data/static/*` or `@/data/http/*` directly.
 */

import { accessTokenCache } from '@/auth/tokenCache';
import { createHttpAdapter } from './http/httpAdapter';
import { createHttpClient } from './http/client';
import type { DataAdapter, DataSourceKind } from './ports';
import { staticAdapter } from './static/staticAdapter';

function resolveKind(): DataSourceKind {
  const configured = import.meta.env.VITE_DATA_SOURCE;
  if (configured === 'http') return 'http';
  if (configured === 'static' || configured === undefined || configured === '') return 'static';
  // A typo in an env var should be loud, not silently fall back to fixtures.
  throw new Error(`VITE_DATA_SOURCE must be "static" or "http" (received "${configured}").`);
}

export const dataSourceKind: DataSourceKind = resolveKind();

function build(): DataAdapter {
  if (dataSourceKind === 'static') return staticAdapter;

  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error('VITE_DATA_SOURCE=http requires VITE_API_BASE_URL to be set.');
  }
  // The bearer is the Cognito ACCESS token the auth provider keeps in the
  // cache; in a build without login the cache is always empty and no header is
  // sent, which is what this client did before there was a login.
  const http = createHttpClient({ baseUrl, getToken: () => accessTokenCache.get() });

  // The Partnership Net Pricing Engine is a separate service with its own origin
  // and its own release cadence (DEC-48). Unset means "same origin as the API",
  // which is the single-deployment case and needs no configuration.
  const pricingBaseUrl = import.meta.env.VITE_PRICING_API_BASE_URL;
  const pricingHttp = pricingBaseUrl
    ? createHttpClient({ baseUrl: pricingBaseUrl, getToken: () => accessTokenCache.get() })
    : http;

  return createHttpAdapter(http, pricingHttp);
}

/** The active adapter. Import this, never an adapter module. */
export const data: DataAdapter = build();

export {
  AGENT_TYPE_OPTIONS,
  agentTypeLabel,
  isAgentTypeEnabled,
  type AgentTypeOption,
} from './documents/agentTypes';
export { buildStoredDocument, type DocumentDraft } from './documents/createDocument';
export { DocumentStoreUnavailableError } from './documents/documentStore';

export type {
  ActivationPort,
  BetaPort,
  CataloguePort,
  DataAdapter,
  DataSourceKind,
  DataQualityPort,
  DocumentsPort,
  HumanLayerProfilePort,
  HumanLayerVisionPort,
  HumanLayerVoicePort,
  LibraryPort,
  MeasurementPort,
  MmmPort,
  PinsPort,
  PricingPort,
  ResearchPort,
  ServicePricingPort,
  StrategicRelevancePort,
  VendorRegisterPort,
} from './ports';
export { DataRequestError, NotImplementedError } from './ports';
export type * from './contracts';
